import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  Group,
  Notification,
  Paper,
  Select,
  Stack,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { useForm, zodResolver } from '@mantine/form';
import { inject, observer } from 'mobx-react';
import { IconCalendar, IconLock } from '@tabler/icons-react';
import * as z from 'zod';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Model, surveyLocalization } from 'survey-core';
import { Survey as SurveyComponent } from 'survey-react-ui';
import { PlainLightPanelless } from 'survey-core/themes';
import 'survey-core/i18n/french';
import { useClinicalNotes } from '../../entities/ClinicalNote/queries/useClinicalNotes';
import { useUpsertClinicalNote } from '../../entities/ClinicalNote/mutations';
import {
  ClinicalNote,
  ClinicalNoteType,
  IClinicalNoteInput,
  clinicalNotesTabKey,
} from '../../entities/ClinicalNote/ClinicalNote';
import { useSessionProvider } from '../../provider/sessionContext';
import { PatientStore } from '../../Store/PatientStore';
import { RouteStore } from '../../Store/RouteStore';
import { AppMedError } from '../../error/AppMedError';
import { useGetQuestionnaireAnswerByTaskIdParam } from '../../entities/QuestionnaireAnswer/queries/useGetQuestionnaireAnswerByTaskIdParam';
import { useGetQuestionnairesByIdParam } from '../../entities/Questionnaires/queries/useGetQuestionnairesByIdParam';

surveyLocalization.supportedLocales = ['en', 'fr'];

/**
 * Per-feature configuration injected by the thin per-type wrapper components
 * (PhysicalExaminationForm, MedicalHistoryForm, CounsellingNoteForm). All
 * differences across the three sibling features flow through this config.
 */
export type ClinicalNoteFormConfig = {
  noteType: ClinicalNoteType;
  /** Translation key prefix, e.g. "physicalExamination" / "medicalHistory" / "counsellingNote". */
  i18nPrefix: string;
  /** Where the Cancel and post-save navigation should land. */
  returnPath: string;
};

export type IClinicalNoteFormProps = {
  config: ClinicalNoteFormConfig;
  patientStore?: PatientStore;
  routeStore?: RouteStore;
};

const ClinicalNoteForm: React.FC<IClinicalNoteFormProps> = inject(
  'patientStore',
  'routeStore',
)(
  observer(({ config, patientStore }: IClinicalNoteFormProps) => {
    const { noteType, i18nPrefix, returnPath } = config;
    const navigate = useNavigate();
    const { currentUser } = useSessionProvider();
    const { t, i18n } = useTranslation();
    const { id: idParam } = useParams<{ id: string }>();

    const editId = Number(idParam);
    const isEdit = Number.isFinite(editId) && editId > 0;

    const patientId = patientStore?.patientId || currentUser?.patientID;
    const resolvedPatientId = patientId != null ? String(patientId) : undefined;

    const { data } = useClinicalNotes(noteType, resolvedPatientId);
    const upsert = useUpsertClinicalNote(
      noteType,
      i18nPrefix,
      resolvedPatientId,
    );

    const note: ClinicalNote | undefined = useMemo(
      () =>
        isEdit ? data?.clinicalNotes.find((e) => e.id === editId) : undefined,
      [isEdit, editId, data?.clinicalNotes],
    );
    const isLocked = !!note?.isLocked;

    const [error, setError] = useState<string | null>(null);
    const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState<
      number | null
    >(null);
    const [hcpAnswers, setHcpAnswers] = useState<Record<string, unknown>>({});
    const noteRef = useRef<HTMLTextAreaElement | null>(null);
    const headerAppendedRef = useRef(false);

    const buildSessionHeader = () => {
      const now = new Date();
      const datePart = now.toLocaleDateString(
        i18n.language === 'fr' ? 'fr-CA' : 'en-CA',
        {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        },
      );
      const timePart = now.toLocaleTimeString(
        i18n.language === 'fr' ? 'fr-CA' : 'en-CA',
        {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        },
      );
      const name = currentUser?.displayName?.trim() || '';
      return `---------------\n${datePart} ${timePart}${name ? ` - ${name}` : ''}\n`;
    };

    const isPreVisitLinked = isEdit && note?.hasPreVisitQuestionnaire;

    const { data: preVisitAnswer } = useGetQuestionnaireAnswerByTaskIdParam(
      isPreVisitLinked ? (note?.patientTaskID ?? null) : null,
    );

    const { data: selectedQuestionnaire } = useGetQuestionnairesByIdParam(
      !isPreVisitLinked ? selectedQuestionnaireId : null,
    );

    const availableQuestionnaires = data?.availableQuestionnaires ?? [];

    const questionnaireOptions = useMemo(
      () =>
        availableQuestionnaires.map((q) => ({
          value: String(q.id),
          label:
            i18n.language === 'fr'
              ? q.nameFr || q.nameEn || ''
              : q.nameEn || q.nameFr || '',
        })),
      [availableQuestionnaires, i18n.language],
    );

    const preVisitSurveyModel = useMemo(() => {
      if (!isPreVisitLinked || !preVisitAnswer?.questionnaireJson) return null;
      const parsedJson = JSON.parse(preVisitAnswer.questionnaireJson);
      const model = new Model(parsedJson);
      const surveyTheme = preVisitAnswer.themeJson
        ? JSON.parse(preVisitAnswer.themeJson)
        : PlainLightPanelless;
      model.applyTheme(surveyTheme);
      model.locale = i18n.language;
      let hasAnswers = false;
      if (preVisitAnswer.answersJson) {
        try {
          const parsedAnswers = JSON.parse(preVisitAnswer.answersJson);
          model.data = parsedAnswers;
          hasAnswers = parsedAnswers && Object.keys(parsedAnswers).length > 0;
        } catch {
          /* ignore */
        }
      }
      if (hasAnswers) model.mode = 'display';
      else {
        model.getAllQuestions().forEach((q) => {
          q.readOnly = true;
        });
      }
      model.questionsOnPageMode = 'singlePage';
      model.showNavigationButtons = false;
      return model;
    }, [isPreVisitLinked, preVisitAnswer, i18n.language]);

    const hcpSurveyModel = useMemo(() => {
      if (isPreVisitLinked || !selectedQuestionnaire?.rawJson) return null;
      const parsedJson = JSON.parse(selectedQuestionnaire.rawJson);
      const model = new Model(parsedJson);
      const surveyTheme = selectedQuestionnaire.themeJson
        ? JSON.parse(selectedQuestionnaire.themeJson)
        : PlainLightPanelless;
      model.applyTheme(surveyTheme);
      model.locale = i18n.language;
      if (hcpAnswers && Object.keys(hcpAnswers).length > 0)
        model.data = hcpAnswers;

      model.questionsOnPageMode = 'singlePage';
      model.showNavigationButtons = false;
      if (isLocked) model.mode = 'display';
      else {
        model.onValueChanged.add((sender) => {
          setHcpAnswers({ ...sender.data });
        });
      }
      return model;
    }, [isPreVisitLinked, selectedQuestionnaire, i18n.language, isLocked]);

    const schema = z.object({
      examDate: z.date(),
      freeTextNote: z.string(),
    });

    const form = useForm({
      mode: 'controlled',
      initialValues: {
        examDate: new Date() as Date | null,
        freeTextNote: '',
      },
      validate: zodResolver(schema),
    });

    useEffect(() => {
      if (headerAppendedRef.current) return;
      // For edit mode, wait until the existing note has loaded so we can
      // append the session header after the prior note instead of on top
      // of an empty string.
      if (isEdit && !note) return;
      headerAppendedRef.current = true;

      if (note && !note.hasPreVisitQuestionnaire)
        setSelectedQuestionnaireId(note.questionnaireID ?? null);

      if (note?.answersJson) {
        try {
          setHcpAnswers(JSON.parse(note.answersJson));
        } catch {
          setHcpAnswers({});
        }
      }

      const existing = note?.freeTextNote ?? '';

      if (isLocked) {
        form.setValues({
          examDate: note?.examDate ?? new Date(),
          freeTextNote: existing,
        });
        return;
      }

      const separator =
        existing.trim() === '' ? '' : existing.endsWith('\n') ? '\n' : '\n\n';
      const header = buildSessionHeader();
      const next = `${existing}${separator}${header}`;

      form.setValues({
        examDate: note?.examDate ?? new Date(),
        freeTextNote: next,
      });

      requestAnimationFrame(() => {
        const el = noteRef.current;
        if (!el) return;
        el.focus();
        const caret = next.length;
        el.setSelectionRange(caret, caret);
        el.scrollTop = el.scrollHeight;
      });
    }, [isEdit, note, isLocked]);

    const goBack = () =>
      navigate(returnPath, {
        state: { clinicalNotesTab: clinicalNotesTabKey(noteType) },
      });

    const handleSubmit = async (values: {
      examDate: Date | null;
      freeTextNote: string;
    }) => {
      if (!resolvedPatientId || !values.examDate) return;
      setError(null);

      const questionnaireID = isPreVisitLinked
        ? (note?.questionnaireID ?? null)
        : selectedQuestionnaireId;
      const hasHcpAnswers = Object.keys(hcpAnswers).length > 0;
      const answersJson = isPreVisitLinked
        ? (note?.answersJson ?? null)
        : hasHcpAnswers
          ? JSON.stringify(hcpAnswers)
          : null;

      const input: IClinicalNoteInput = {
        id: isEdit ? editId : null,
        examDate: values.examDate,
        questionnaireID,
        patientTaskID: note?.patientTaskID ?? null,
        freeTextNote:
          values.freeTextNote.trim() === '' ? null : values.freeTextNote,
        answersJson,
      };
      try {
        await upsert.mutateAsync({ patientId: resolvedPatientId, input });
        goBack();
      } catch (e) {
        if (e instanceof AppMedError) setError(e.message);
        else setError('An unexpected error occurred.');
      }
    };

    const dateInput = (
      <DateTimePicker
        label={t(`${i18nPrefix}.editModal.examDate`)}
        placeholder="YYYY-MM-DD HH:mm"
        valueFormat="YYYY-MM-DD HH:mm"
        leftSection={<IconCalendar size={16} />}
        withAsterisk
        disabled={isLocked}
        {...form.getInputProps('examDate')}
      />
    );

    const noteTextarea = (label: string, placeholderKey: string) => (
      <Textarea
        label={label}
        placeholder={t(placeholderKey)}
        autosize
        minRows={6}
        readOnly={isLocked}
        ref={noteRef}
        {...form.getInputProps('freeTextNote')}
      />
    );

    return (
      <Paper h={'min-content'} w={'100%'} p="md">
        <Title order={4} mb="md">
          {isLocked
            ? t(
                `${i18nPrefix}.editModal.consultTitle`,
                t(`${i18nPrefix}.rowActions.consult`),
              )
            : isEdit
              ? t(`${i18nPrefix}.editModal.editTitle`)
              : t(`${i18nPrefix}.editModal.createTitle`)}
        </Title>
        {isLocked && (
          <Alert color="blue" icon={<IconLock size={16} />} mb="md">
            {t(`${i18nPrefix}.editModal.lockedNotice`)}
          </Alert>
        )}
        <form
          onSubmit={form.onSubmit((values) => {
            handleSubmit(values);
          })}
        >
          {isPreVisitLinked ? (
            <Grid gutter="md">
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Stack gap="sm">
                  {dateInput}
                  {noteTextarea(
                    t(`${i18nPrefix}.editModal.medicalNote`),
                    `${i18nPrefix}.editModal.medicalNotePlaceholder`,
                  )}
                </Stack>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Box
                  p="sm"
                  style={{
                    border: '1px solid var(--mantine-color-gray-3)',
                    borderRadius: 4,
                  }}
                >
                  <Group justify="space-between" mb="xs">
                    <Title order={5}>
                      {note?.questionnaireName ??
                        t(`${i18nPrefix}.editModal.patientAnswers`)}
                    </Title>
                    <Badge
                      color={note?.patientTaskCompleted ? 'green' : 'yellow'}
                      variant="light"
                    >
                      {note?.patientTaskCompleted
                        ? t(`${i18nPrefix}.preVisit.completed`)
                        : t(`${i18nPrefix}.preVisit.pending`)}
                    </Badge>
                  </Group>
                  {preVisitSurveyModel ? (
                    <SurveyComponent model={preVisitSurveyModel} />
                  ) : (
                    <Text size="sm" c="dimmed">
                      {t(`${i18nPrefix}.editModal.noAnswersYet`)}
                    </Text>
                  )}
                </Box>
              </Grid.Col>
            </Grid>
          ) : (
            (() => {
              const questionnaireSelect = (
                <Select
                  label={t(`${i18nPrefix}.editModal.questionnaire`)}
                  description={t(`${i18nPrefix}.editModal.questionnaireHelp`)}
                  placeholder={t(
                    `${i18nPrefix}.editModal.questionnairePlaceholder`,
                  )}
                  data={questionnaireOptions}
                  value={
                    selectedQuestionnaireId != null
                      ? String(selectedQuestionnaireId)
                      : null
                  }
                  onChange={(value) => {
                    setSelectedQuestionnaireId(value ? Number(value) : null);
                    setHcpAnswers({});
                  }}
                  clearable={!isLocked}
                  searchable={!isLocked}
                  disabled={isLocked}
                />
              );

              const noteLabel = selectedQuestionnaireId
                ? t(`${i18nPrefix}.editModal.additionalNote`)
                : t(`${i18nPrefix}.editModal.medicalNote`);
              const notePlaceholderKey = selectedQuestionnaireId
                ? `${i18nPrefix}.editModal.additionalNotePlaceholder`
                : `${i18nPrefix}.editModal.medicalNotePlaceholder`;

              const leftStack = (
                <Stack gap="sm">
                  {dateInput}
                  {questionnaireSelect}
                  {noteTextarea(noteLabel, notePlaceholderKey)}
                </Stack>
              );

              return hcpSurveyModel ? (
                <Grid gutter="md">
                  <Grid.Col span={{ base: 12, md: 6 }}>{leftStack}</Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Box
                      p="sm"
                      style={{
                        border: '1px solid var(--mantine-color-gray-3)',
                        borderRadius: 4,
                      }}
                    >
                      <SurveyComponent model={hcpSurveyModel} />
                    </Box>
                  </Grid.Col>
                </Grid>
              ) : (
                leftStack
              );
            })()
          )}

          {noteType === ClinicalNoteType.Counselling &&
            note &&
            (note.hcpSigned || note.patientAcknowledged) && (
              <Box
                mt="lg"
                p="sm"
                style={{
                  border: '1px solid var(--mantine-color-gray-3)',
                  borderRadius: 4,
                }}
              >
                <Title order={5} mb="xs">
                  {t(`${i18nPrefix}.editModal.acknowledgements.title`)}
                </Title>
                <Stack gap="xs">
                  <Text size="sm">
                    {note.hcpSigned && note.hcpSignedAtUtc
                      ? t(
                          `${i18nPrefix}.editModal.acknowledgements.hcpSignedBy`,
                          {
                            name: note.hcpSignedByName ?? '',
                            date: new Date(
                              note.hcpSignedAtUtc,
                            ).toLocaleDateString(
                              i18n.language === 'fr' ? 'fr-CA' : 'en-CA',
                              { dateStyle: 'medium' },
                            ),
                          },
                        )
                      : t(
                          `${i18nPrefix}.editModal.acknowledgements.hcpNotSigned`,
                        )}
                  </Text>
                  <Text size="sm">
                    {note.patientAcknowledged && note.patientAcknowledgedAtUtc
                      ? t(
                          `${i18nPrefix}.editModal.acknowledgements.patientAcknowledgedBy`,
                          {
                            name: note.patientAcknowledgedByName ?? '',
                            date: new Date(
                              note.patientAcknowledgedAtUtc,
                            ).toLocaleDateString(
                              i18n.language === 'fr' ? 'fr-CA' : 'en-CA',
                              { dateStyle: 'medium' },
                            ),
                          },
                        )
                      : t(
                          `${i18nPrefix}.editModal.acknowledgements.patientNotAcknowledged`,
                        )}
                  </Text>
                  {note.patientAcknowledged && (
                    <Box mt="xs">
                      <Text size="sm" fw={500}>
                        {t(
                          `${i18nPrefix}.editModal.acknowledgements.patientNotes`,
                        )}
                      </Text>
                      {note.patientAcknowledgementNotes &&
                      note.patientAcknowledgementNotes.trim() !== '' ? (
                        <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                          {note.patientAcknowledgementNotes}
                        </Text>
                      ) : (
                        <Text size="sm" c="dimmed">
                          {t(
                            `${i18nPrefix}.editModal.acknowledgements.patientNoNotes`,
                          )}
                        </Text>
                      )}
                    </Box>
                  )}
                </Stack>
              </Box>
            )}

          {error && (
            <Notification mt={'md'} color="red" onClose={() => setError(null)}>
              {error}
            </Notification>
          )}

          <Flex
            gap={'sm'}
            justify={'flex-end'}
            w={'100%'}
            mt="md"
            py="sm"
            style={{
              position: 'sticky',
              bottom: 0,
              background: 'var(--mantine-color-body)',
              borderTop: '1px solid var(--mantine-color-gray-3)',
              zIndex: 10,
            }}
          >
            <Button
              disabled={upsert.isPending}
              type={'button'}
              onClick={goBack}
              variant="default"
            >
              {isLocked
                ? t('button.close', t('button.cancel'))
                : t('button.cancel')}
            </Button>
            {!isLocked && (
              <Button
                disabled={upsert.isPending}
                loading={upsert.isPending}
                type={'submit'}
              >
                {t(`${i18nPrefix}.editModal.save`)}
              </Button>
            )}
          </Flex>
        </form>
      </Paper>
    );
  }),
);

export default ClinicalNoteForm;
